/**
 * Vista 5 · Bloque — nivel 1.
 *
 * Los filtros son **funcionales**, a diferencia del prototipo, donde eran
 * decorativos (`P-13`). Filtran en cliente sobre la proyección que ya trae el
 * estado agregado de cada expediente: no hay una segunda verdad que calcular.
 */
import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, ProgressBar, StatusBadge } from '@mpfn/ui'
import { Button } from '@mpfn/ui'
import {
  DataTable,
  EmptyState,
  FileUploadField,
  FilterBar,
  FormField,
  PageHeader,
  SearchBar,
  StatCard,
  type DataTableColumn,
} from '@mpfn/patterns'
import { NextActionCard } from '@mpfn/patterns/experimental'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mpfn/ui'
import { Download, FileText, FolderSearch, Upload } from 'lucide-react'
import { urlDeActa, urlDeArchivo } from '../api/cliente.js'
import { useBloques, useExpedientesDeBloque, useSorteo, useSubirActaFirmada } from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { EstadoDocumento, ExpedienteResumen, SeccionVerificable } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { ConAyuda } from '../componentes/ConAyuda.js'
import { Seguimiento } from '../componentes/Seguimiento.js'
import { SeguimientoDeSeccion } from '../componentes/SeguimientoDeSeccion.js'
import { irA } from '../navegacion/Enlace.js'
import { usePuede } from '../sesion/contexto.js'
import { avisarError, avisarExito } from '../lib/avisos.js'
import { dni as formatearDni, fecha, numero, periodo as formatearPeriodo } from '../lib/formato.js'

const TODOS = 'todos'

export function Bloque({ periodo }: { periodo: string }) {
  const catalogos = useCatalogos()
  const bloques = useBloques()
  const puedeSortear = usePuede('sorteo.ejecutar')
  const puedeRegistrar = usePuede('documento_declarado.registrar')

  const bloque = (bloques.data ?? []).find((item) => item.periodo === periodo)
  const expedientes = useExpedientesDeBloque(bloque?.id)

  const [busqueda, setBusqueda] = useState('')
  const [seccion, setSeccion] = useState<string>(TODOS)
  const [estado, setEstado] = useState<string>(TODOS)

  const columnas: ReadonlyArray<DataTableColumn<ExpedienteResumen>> = useMemo(
    () => [
      {
        id: 'trabajador',
        header: 'Trabajador',
        cell: (fila) => (
          <span className="sigfisc-celda-trabajador">
            <strong>{fila.nombres}</strong>
            <span className="sigfisc-celda-trabajador__meta">
              {fila.regimen} · {formatearDni(fila.dni)}
            </span>
          </span>
        ),
      },
      {
        id: 'secciones',
        header: 'Secciones',
        cell: (fila) => (
          <span className="sigfisc-chips">
            {/* Selección no aparece: no genera requerimiento (`NEG-1`) y su
                avance se lee en la columna de la derecha. */}
            {fila.secciones.map((item) => {
              const nombre = etiquetaDe(catalogos.secciones, item.seccion)
              const insignia = (
                <StatusBadge
                  tone={item.tono}
                  label={nombre.charAt(0)}
                  aria-label={`${nombre}: ${item.etiqueta}`}
                />
              )

              /**
               * Solo es disparador la sección que TIENE circuito detrás.
               *
               * `sin_requerir` es el único estado sin ningún requerimiento
               * emitido: abrir un panel ahí enseñaría una tarjeta vacía, y un
               * control que no lleva a nada es peor que ninguno. Además
               * preserva la razón por la que estos chips nunca fueron
               * tabulables: son tres por fila, y en un bloque de treinta
               * trabajadores hacerlos todos enfocables añadiría noventa
               * paradas. Así solo entran al recorrido las que dicen algo.
               */
              if (item.estado === 'sin_requerir') {
                return (
                  <ConAyuda
                    key={item.seccion}
                    ayuda={
                      <>
                        <strong>{nombre}</strong>
                        <span className="sigfisc-ayuda__estado">{item.etiqueta}</span>
                      </>
                    }
                  >
                    {insignia}
                  </ConAyuda>
                )
              }

              // Con circuito, el chip enseña POR QUÉ está en ese estado: los
              // documentos de la sección con sus requerimientos y sus pasos,
              // sin entrar al expediente. El detalle se pide al abrirse.
              return (
                <Seguimiento
                  key={item.seccion}
                  etiqueta={`${nombre} de ${fila.nombres}: ${item.etiqueta}. Ver el circuito`}
                  disparador={insignia}
                >
                  <SeguimientoDeSeccion
                    expedienteId={fila.id}
                    seccion={item.seccion}
                    titulo={`${nombre} · ${fila.nombres}`}
                  />
                </Seguimiento>
              )
            })}
          </span>
        ),
      },
      {
        id: 'avance',
        header: 'Avance',
        hideOnMobile: true,
        cell: (fila) => (
          <span className="sigfisc-avance">
            <ProgressBar
              value={fila.avance.total === 0 ? 0 : Math.round((fila.avance.verificados / fila.avance.total) * 100)}
              label={`Avance de ${fila.nombres}`}
            />
            <span className="sigfisc-avance__cifra">
              {numero(fila.avance.verificados)}/{numero(fila.avance.total)}
            </span>
          </span>
        ),
      },
      {
        id: 'estado',
        header: 'Estado',
        cell: (fila) => <StatusBadge tone={fila.tono} label={fila.etiqueta} />,
      },
    ],
    [catalogos],
  )

  return (
    <Pagina>
      <Datos estado={expedientes}>
        {(lista) => {
          const filtrados = lista.filter((fila) => {
            if (estado !== TODOS && fila.estado !== (estado as EstadoDocumento)) return false
            if (seccion !== TODOS) {
              const encontrada = fila.secciones.find((item) => item.seccion === (seccion as SeccionVerificable))
              if (!encontrada || encontrada.estado === 'sin_requerir') return false
            }
            const texto = busqueda.trim().toLowerCase()
            if (texto && !`${fila.nombres} ${fila.dni}`.toLowerCase().includes(texto)) return false
            return true
          })

          const totales = lista.reduce(
            (acumulado, fila) => ({
              documentos: acumulado.documentos + fila.documentos,
              verificados: acumulado.verificados + (fila.estado === 'verificado' ? 1 : 0),
              enProceso: acumulado.enProceso + (fila.estado === 'en_verificacion' ? 1 : 0),
              irregulares: acumulado.irregulares + (fila.estado === 'irregular' ? 1 : 0),
            }),
            { documentos: 0, verificados: 0, enProceso: 0, irregulares: 0 },
          )

          const primero = lista[0]

          const avanceTotal = lista.reduce((suma, fila) => suma + fila.avance.total, 0)
          const avanceHecho = lista.reduce((suma, fila) => suma + fila.avance.verificados, 0)

          return (
            <>
              <PageHeader
                title={`Bloque ${formatearPeriodo(periodo)}`}
                description={`${numero(lista.length)} trabajadores · ${numero(totales.documentos)} documentos declarados`}
                action={
                  bloque ? (
                    <StatusBadge
                      tone={bloque.estado === 'en_fiscalizacion' ? 'info' : 'neutral'}
                      label={etiquetaDe(catalogos.estadosBloque, bloque.estado)}
                    />
                  ) : undefined
                }
              />

              {avanceTotal > 0 && (
                <ProgressBar
                  value={Math.round((avanceHecho / avanceTotal) * 100)}
                  label="Avance del bloque"
                />
              )}

              <div className="sigfisc-kpis">
                <StatCard label="Documentos" value={numero(totales.documentos)} />
                <StatCard label="Expedientes verificados" value={numero(totales.verificados)} />
                <StatCard label="En proceso" value={numero(totales.enProceso)} />
                <StatCard label="Con irregularidad" value={numero(totales.irregulares)} />
              </div>

              {bloque?.estado === 'en_fiscalizacion' && bloque.sorteoId !== undefined && (
                <ActaDelSorteo sorteoId={bloque.sorteoId} />
              )}

              {/**
               * El bloque recién sorteado: hay trabajadores pero ningún
               * documento que fiscalizar todavía.
               *
               * Es el momento en que el analista no sabe qué hacer —los
               * expedientes nacen con las secciones vacías y nada en pantalla
               * lo dice—. Va aquí, persistente, y no en un aviso que se
               * desvanece a los cinco segundos: es la política de Recuperos
               * §10 para un resultado con próximos pasos importantes.
               *
               * `NextActionCard` y no un `Callout`, que es lo que había: el
               * design system trae una pieza para exactamente esto —«handoff
               * visual de próximos pasos»— y usar la genérica lo anunciaba
               * como una nota informativa más, del mismo color que «derivado
               * al OCI». Aquí no se informa de un estado: se dice qué hacer a
               * continuación, y la tarjeta lo dice con su flecha y su fondo de
               * selección. `UI-5`.
               */}
              {lista.length > 0 && totales.documentos === 0 && (
                <NextActionCard
                  title="Los expedientes están creados y vacíos"
                  description="Cada trabajador empieza con la sección Selección por verificar y las de Formación, Experiencia y Capacitación sin documentos. El siguiente paso es abrir un expediente y registrar lo que presentó; hasta entonces no hay nada que requerir."
                  {...(puedeRegistrar && primero
                    ? {
                        action: (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                              irA('expediente', { id: primero.id }, { periodo })
                            }}
                          >
                            Empezar por {primero.nombres.split(',')[0] ?? 'el primero'}
                          </Button>
                        ),
                      }
                    : {})}
                />
              )}

              {lista.length === 0 ? (
                <EmptyState
                  icon={FolderSearch}
                  title="Este bloque todavía no tiene expedientes"
                  description="Los expedientes se crean al confirmar la selección de muestra."
                  {...(puedeSortear
                    ? {
                        action: (
                          <Button
                            variant="primary"
                            onClick={() => {
                              irA('sorteo')
                            }}
                          >
                            Ir a la selección de muestra
                          </Button>
                        ),
                      }
                    : {})}
                />
              ) : (
                <DataTable
                  columns={columnas}
                  rows={filtrados}
                  rowKey={(fila) => fila.id}
                  onRowClick={(fila) => {
                    irA('expediente', { id: fila.id }, { periodo })
                  }}
                  rowActionLabel={(fila) => `Abrir el expediente de ${fila.nombres}`}
                  empty={{
                    title: 'Ningún expediente coincide con el filtro',
                    description: 'Pruebe con otro estado o limpie la búsqueda.',
                  }}
                  toolbar={
                    <FilterBar
                      primary={
                        <SearchBar
                          value={busqueda}
                          onChange={setBusqueda}
                          label="Buscar trabajador"
                          placeholder="Nombre o DNI"
                        />
                      }
                      filters={
                        <>
                          <Select value={seccion} onValueChange={setSeccion}>
                            <SelectTrigger aria-label="Filtrar por sección">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={TODOS}>Todas las secciones</SelectItem>
                              {catalogos.secciones.map((opcion) => (
                                <SelectItem key={opcion.codigo} value={opcion.codigo}>
                                  {opcion.etiqueta}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          <Select value={estado} onValueChange={setEstado}>
                            <SelectTrigger aria-label="Filtrar por estado">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={TODOS}>Todos los estados</SelectItem>
                              {catalogos.estadosDocumento.map((opcion) => (
                                <SelectItem key={opcion.codigo} value={opcion.codigo}>
                                  {opcion.etiqueta}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      }
                    />
                  }
                />
              )}
            </>
          )
        }}
      </Datos>
    </Pagina>
  )
}

/**
 * El acta del sorteo: el documento del bloque.
 *
 * Se emite al confirmar la muestra. El área la firma digitalmente fuera del
 * sistema y la adjunta aquí, donde queda como evidencia del sorteo con su
 * huella; mientras falte, la tarjeta lo dice.
 */
function ActaDelSorteo({ sorteoId }: { sorteoId: string }) {
  const resultado = useSorteo(sorteoId)
  const subir = useSubirActaFirmada()
  const puedeAdjuntar = usePuede('sorteo.confirmar')
  const [archivo, setArchivo] = useState<File | null>(null)

  const sorteo = resultado.data?.sorteo
  const numeroActa = sorteo?.numeroActa
  // Los bloques confirmados antes del 07/10/2026 no tienen acta del sistema:
  // la suya se firmó a mano.
  if (!sorteo || numeroActa === undefined) return null
  const firmada = sorteo.actaEvidenciaId

  return (
    <Card>
      <CardHeader>
        <CardTitle>Acta de selección de muestra</CardTitle>
      </CardHeader>
      <CardContent className="sigfisc-formulario">
        <p className="sigfisc-nota">
          Acta N.° <strong>{numeroActa}</strong> · emitida el {fecha(sorteo.confirmadoEn)} ·{' '}
          {firmada !== undefined ? 'firmada y adjunta' : 'falta adjuntar la firmada'}
        </p>

        <div className="sigfisc-acciones">
          <Button variant="secondary" size="sm" asChild>
            <a href={urlDeActa(sorteo.id)} target="_blank" rel="noopener noreferrer">
              <FileText size={14} aria-hidden="true" />
              Ver el acta emitida
            </a>
          </Button>
          {firmada !== undefined && (
            <Button variant="secondary" size="sm" asChild>
              <a href={urlDeArchivo(firmada)} download>
                <Download size={14} aria-hidden="true" />
                Descargar el acta firmada
              </a>
            </Button>
          )}
        </div>

        {firmada === undefined && puedeAdjuntar && (
          <>
            <FormField
              id="acta-firmada"
              label="Acta firmada digitalmente"
              hint="El PDF con los V°B°. Queda como evidencia de la selección, con su huella."
            >
              <FileUploadField
                id="acta-firmada"
                value={archivo}
                onChange={setArchivo}
                accept=".pdf"
                acceptLabel="PDF"
                maxSizeMb={20}
                variant="compact"
                onError={(mensaje) => {
                  avisarError(new Error(mensaje), 'El archivo no es válido')
                }}
              />
            </FormField>
            <div className="sigfisc-acciones">
              <Button
                variant="primary"
                size="sm"
                loading={subir.isPending}
                disabled={archivo === null}
                onClick={() => {
                  if (!archivo) return
                  subir.mutate(
                    { sorteoId: sorteo.id, archivo },
                    {
                      onSuccess: () => {
                        setArchivo(null)
                        avisarExito('Acta firmada adjunta.', `Acta N.° ${numeroActa}`)
                      },
                      onError: (error) => {
                        avisarError(error, 'No se pudo adjuntar el acta firmada')
                      },
                    },
                  )
                }}
              >
                <Upload size={14} aria-hidden="true" />
                Adjuntar el acta firmada
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
